import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { maps } from '../data/maps';

const NavBar: React.FC = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const getBreadcrumbText = (segment: string, index: number) => {
    if (index === 0 && segment === 'map') return 'Maps';
    if (index === 1) {
      const map = maps[segment];
      return map ? map.displayName : segment;
    }
    if (index === 2) {
      return segment === 'CT' ? 'Counter-Terrorist' : 'Terrorist';
    }
    return segment;
  };

  const getBreadcrumbLink = (segment: string, index: number) => {
    if (index === 0) return '/';
    if (index === 1) return `/map/${segment}`;
    if (index === 2) return `/map/${pathSegments[1]}/${segment}`;
    return '#';
  };

  const canGoBack = pathSegments.length > 0;

  return (
    <nav className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700 shadow-xl sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Logo and Home */}
          <div className="flex items-center gap-2 sm:gap-6 flex-shrink-0">
            <Link 
              to="/" 
              className="flex items-center gap-2 sm:gap-3 group hover:scale-105 transition-transform duration-200"
            >
              <img 
                src="/cs2-logo.webp" 
                alt="CS2 Logo" 
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-md shadow-lg"
              />
              <div className="flex flex-col">
                <span className="text-yellow-400 font-bold text-lg sm:text-xl leading-tight">
                  CS2 Lineups
                </span>
                <span className="text-gray-400 text-xs font-medium hidden sm:block">
                  Master the Art of Utility
                </span>
              </div>
            </Link>
            
            {canGoBack && (
              <button
                onClick={() => window.history.back()}
                className="flex items-center gap-1 sm:gap-2 text-gray-300 hover:text-white px-2 sm:px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-xs sm:text-sm font-medium transition-all duration-200 hover:shadow-md"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back</span>
              </button>
            )}
          </div>

          {/* Center - Submit Clip Button (Desktop only) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 hidden sm:block">
            <Link
              to="/submit-clip"
              className="flex items-center gap-2 bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg font-semibold hover:bg-yellow-300 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-yellow-400/25 text-base"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit Clip
            </Link>
          </div>

          {/* Right side - Submit Button (Mobile) and Breadcrumbs (Desktop) */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile Submit Button */}
            <Link
              to="/submit-clip"
              className="flex items-center gap-1 bg-yellow-400 text-gray-900 px-3 py-2 rounded-lg font-semibold hover:bg-yellow-300 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-yellow-400/25 text-sm sm:hidden"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit
            </Link>

                        {/* Desktop Breadcrumbs */}
            <div className="hidden sm:flex items-center gap-3 text-sm bg-gray-800/50 px-4 py-2 rounded-lg border border-gray-700 min-w-0 h-10 flex-shrink-0">
              {pathSegments.length > 0 ? (
                <>
                  <Link 
                    to="/" 
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 hover:scale-105 h-6"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span className="whitespace-nowrap">Home</span>
                  </Link>
                  
                  {pathSegments.map((segment, index) => {
                    const isLast = index === pathSegments.length - 1;
                    const link = getBreadcrumbLink(segment, index);
                    const text = getBreadcrumbText(segment, index);
                    
                    return (
                      <React.Fragment key={index}>
                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {isLast ? (
                          <span className="text-yellow-400 font-semibold px-2 py-1 bg-yellow-400/10 rounded-md h-6 flex items-center whitespace-nowrap text-sm">
                            {text}
                          </span>
                        ) : (
                          <Link 
                            to={link}
                            className="text-gray-400 hover:text-white transition-colors duration-200 hover:scale-105 px-2 py-1 rounded-md hover:bg-gray-700/50 h-6 flex items-center whitespace-nowrap text-sm"
                          >
                            {text}
                          </Link>
                        )}
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                <div className="text-gray-500 text-sm flex items-center h-full whitespace-nowrap">Home</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar; 